import { ChangeEvent, useState } from "react";
import { BUGMEN_TABS } from "./bugmen";

import "./styles.css";

const emptyString = Array(32).fill("-");

const getDefaultState = () => [
  {
    name: "",
    strings: [
      {
        name: "E",
        tab: [...emptyString],
      },
      {
        name: "B",
        tab: [...emptyString],
      },
      {
        name: "G",
        tab: [...emptyString],
      },
      {
        name: "D",
        tab: [...emptyString],
      },
      {
        name: "A",
        tab: [...emptyString],
      },
      {
        name: "E",
        tab: [...emptyString],
      },
    ],
  },
];

const CHROMATIC_SCALE = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

const downloadTxt = (content: string) => {
  const file = new Blob([content], { type: "text/plain;charset=utf-8" });
  return URL.createObjectURL(file);
};

const isInteger = (value: string) => {
  return /^\d+$/.test(value);
};

export default function App() {
  const [tabState, setTabState] = useState(getDefaultState());
  const [songName, setSongName] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noteName, setNoteName] = useState("?");

  const handleTabChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const tabName = e.target.value;
    const selectedTab = BUGMEN_TABS.find((tab) => tab.name === tabName);
    if (selectedTab) {
      setTabState(selectedTab.tab);
      setSongName(selectedTab.name);
      return;
    }
    setTabState(getDefaultState());
    setSongName("");
  };

  const readFile = (e: any) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target) {
        const contents = JSON.parse(e.target.result as any);
        setTabState(contents.tab);
        setSongName(contents.name);
      }
      setLoading(false);
    };
    reader.readAsText(file);
    setLoading(true);
  };

  const updateNoteName = ({
    note,
    string,
  }: {
    note: string;
    string: string;
  }) => {
    if (!isInteger(note)) {
      setNoteName("?");
      return;
    }
    const fret = parseInt(note, 10);
    const stringNoteIndex = CHROMATIC_SCALE.indexOf(string);
    const absoluteIndex = fret + stringNoteIndex;
    const newNoteIndex = absoluteIndex % 12;
    setNoteName(CHROMATIC_SCALE[newNoteIndex]);
  };

  const handleChange = ({ event, partIndex, stringIndex, noteIndex }: any) => {
    const fret = event.target.value;
    const newState = [...tabState];
    newState[partIndex].strings[stringIndex].tab[noteIndex] =
      fret === "" ? "-" : fret;
    setTabState(newState);
  };

  const renamePart = ({ event, partIndex }: any) => {
    const name = event.target.value;
    const newState = [...tabState];
    newState[partIndex].name = name;
    setTabState(newState);
  };

  const handleKeyboard = ({ e, partIndex, stringIndex, noteIndex }: any) => {
    if (e.code === "Backspace") {
      const newState = [...tabState];
      newState[partIndex].strings[stringIndex].tab[noteIndex] = "-";
      setTabState(newState);
    }
    if (e.shiftKey || e.metaKey) {
      if (e.code === "ArrowLeft" && noteIndex > 0) {
        const newState = [...tabState];
        newState[partIndex].strings[stringIndex].tab[noteIndex - 1] =
          newState[partIndex].strings[stringIndex].tab[noteIndex];
        newState[partIndex].strings[stringIndex].tab[noteIndex] = "-";
        setTabState(newState);
        const element = document.getElementById(
          `${partIndex}${stringIndex}${noteIndex - 1}`
        );
        if (element) {
          element.focus();
        }
      }
      if (
        e.code === "ArrowRight" &&
        noteIndex < tabState[0].strings[0].tab.length - 1
      ) {
        const newState = [...tabState];
        newState[partIndex].strings[stringIndex].tab[noteIndex + 1] =
          newState[partIndex].strings[stringIndex].tab[noteIndex];
        newState[partIndex].strings[stringIndex].tab[noteIndex] = "-";
        setTabState(newState);
        const element = document.getElementById(
          `${partIndex}${stringIndex}${noteIndex + 1}`
        );
        if (element) {
          element.focus();
        }
      }
      return;
    }
    if (e.code === "ArrowLeft") {
      const element = document.getElementById(
        `${partIndex}${stringIndex}${noteIndex - 1}`
      );
      if (element) {
        element.focus();
      }
    }
    if (e.code === "ArrowRight") {
      const element = document.getElementById(
        `${partIndex}${stringIndex}${noteIndex + 1}`
      );
      if (element) {
        element.focus();
      }
    }
    if (e.code === "ArrowUp") {
      const element = document.getElementById(
        `${partIndex}${stringIndex - 1}${noteIndex}`
      );
      if (element) {
        element.focus();
      }
    }
    if (e.code === "ArrowDown") {
      const element = document.getElementById(
        `${partIndex}${stringIndex + 1}${noteIndex}`
      );
      if (element) {
        element.focus();
      }
    }
  };

  const addPart = () => {
    const newState = [...tabState, ...getDefaultState()];
    setTabState(newState);
  };

  const getPreview = () => {
    const textLines: string[] = [`${songName || "Tab"}\n\n`];
    tabState.forEach((part) => {
      if (part.name) {
        textLines.push(`${part.name}\n`);
      }
      part.strings.forEach((str) => {
        const parsed = str.tab.map((txt) => {
          if (txt.length === 1) {
            return `-${txt}`;
          }
          return `${txt}`;
        });
        const notes = parsed.join("");
        textLines.push(`${str.name}|${notes}\n`);
      });
      textLines.push("\n");
    });
    const result = "".concat(...textLines);
    return result;
  };

  const Preview = () => {
    const previewText = getPreview();
    const linkTxt = downloadTxt(previewText);
    const expandedState = { name: songName, tab: tabState };
    const linkJson = downloadTxt(JSON.stringify(expandedState));
    return (
      <div className="preview">
        <pre>{previewText}</pre>
        <div className="controls">
          <a
            download={songName ? `${songName}.txt` : "untitled.txt"}
            href={linkTxt}
            onClick={() => setShowPreview(false)}
          >
            Save as TXT
          </a>
          <a
            download={songName ? `${songName}.tab` : "untitled.tab"}
            href={linkJson}
            onClick={() => setShowPreview(false)}
          >
            Save as TAB
          </a>
          <button onClick={() => setShowPreview(false)}>Close preview</button>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <h1>SimpleTab</h1>
      <div style={{ margin: "8px 0" }}>
        <label htmlFor="librarySelect">Library</label>
        <div>
          <select onChange={handleTabChange} name="librarySelect">
            <option value="">Not selected</option>
            {BUGMEN_TABS.map((tab) => (
              <option key={tab.name} value={tab.name}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {showPreview && <Preview />}
      {loading && <div>Loading...</div>}
      {!showPreview && !loading && (
        <div className="editor">
          <input
            className={`title ${songName ? "filledName" : undefined}`}
            value={songName}
            placeholder={"song name"}
            onChange={(e) => setSongName(e.target.value)}
          />
          <div className="noteName">Current note: {noteName}</div>
          {tabState.map((part, partIndex) => (
            <div className="wrapper" key={`part-${partIndex}`}>
              <div className="partName">
                <input
                  className={part.name ? "filledName" : undefined}
                  type="text"
                  value={part.name}
                  placeholder={"part name"}
                  onChange={(event) => renamePart({ event, partIndex })}
                />
              </div>
              {part.strings.map((str, stringIndex) => (
                <div
                  className="string"
                  key={`string-${partIndex}-${stringIndex}`}
                >
                  <div className="stringName">{`${str.name}︱`}</div>
                  {str.tab.map((note, noteIndex) => (
                    <input
                      id={`${partIndex}${stringIndex}${noteIndex}`}
                      key={`${partIndex}${stringIndex}${noteIndex}`}
                      type="text"
                      value={note === "-" ? "" : note}
                      placeholder={"━"}
                      onFocus={() => updateNoteName({ note, string: str.name })}
                      onKeyDown={(e) =>
                        handleKeyboard({ e, partIndex, stringIndex, noteIndex })
                      }
                      onChange={(event) => {
                        handleChange({
                          event,
                          partIndex,
                          stringIndex,
                          noteIndex,
                        });
                        updateNoteName({
                          note: event.target.value,
                          string: str.name,
                        });
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
          <div className="addPart">
            <button type="button" onClick={addPart}>
              + Add part
            </button>
          </div>
          <div className="save">
            <button type="button" onClick={() => setShowPreview(true)}>
              PREVIEW&SAVE
            </button>
          </div>
          <div className="save">
            <button>
              <label htmlFor="loadfile">Load</label>
            </button>
            <input
              accept=".tab"
              name="loadfile"
              type="file"
              id="loadfile"
              disabled={loading}
              onChange={readFile}
            />
          </div>
          <p className="help">
            When editing, press <b>Arrow Keys</b> to navigate.
            <br />
            Press <b>Shift</b> or <b>Command</b> + <b>Left</b> or <b>Right</b>{" "}
            to move a selected note.
          </p>
        </div>
      )}
    </div>
  );
}
